<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRoute, RouterLink } from 'vue-router'
import { authApi } from '@/api'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import AuthField from '@/components/auth/AuthField.vue'
import { getErrorStatus } from '@/utils/error'

const route = useRoute()
const token = computed(() => (route.query.token as string) || '')

const password = ref('')
const confirmPassword = ref('')
const isLoading = ref(false)
const errorMessage = ref('')
const isSuccess = ref(false)

const passwordsMatch = computed(() => password.value === confirmPassword.value)
const isFormValid = computed(
  () => token.value.length > 0 && password.value.length >= 8 && passwordsMatch.value,
)

const passwordStrength = computed(() => {
  const pwd = password.value
  if (!pwd) return { level: 0, text: '', bar: '', textCls: 'text-ink-400' }
  if (pwd.length < 8) return { level: 1, text: 'Too short', bar: 'bg-danger-ink', textCls: 'text-danger-ink' }
  let s = 0
  if (pwd.length >= 8) s++
  if (/[a-z]/.test(pwd) && /[A-Z]/.test(pwd)) s++
  if (/\d/.test(pwd)) s++
  if (/[^a-zA-Z0-9]/.test(pwd)) s++
  if (s <= 2) return { level: 2, text: 'Weak', bar: 'bg-warning-ink', textCls: 'text-warning-ink' }
  if (s === 3) return { level: 3, text: 'Good', bar: 'bg-info-ink', textCls: 'text-info-ink' }
  return { level: 4, text: 'Strong', bar: 'bg-success-ink', textCls: 'text-success-ink' }
})

async function handleResetPassword() {
  if (!isFormValid.value) return
  errorMessage.value = ''
  isLoading.value = true
  try {
    await authApi.authResetPasswordPost({ token: token.value, newPassword: password.value })
    isSuccess.value = true
  } catch (e: unknown) {
    console.error('Reset password failed:', e)
    errorMessage.value =
      getErrorStatus(e) === 401
        ? 'This reset link is invalid or has expired. Please request a new one.'
        : 'Something went wrong. Please try again.'
  } finally {
    isLoading.value = false
  }
}
</script>

<template>
  <div class="w-full max-w-md">
    <!-- No token state -->
    <template v-if="!token">
      <div class="text-center">
        <div class="w-16 h-16 bg-danger-soft rounded-2xl flex items-center justify-center mx-auto mb-6">
          <font-awesome-icon :icon="['fas', 'link-slash']" class="text-3xl text-danger-ink" />
        </div>
        <h1 class="text-2xl font-bold text-ink-900 mb-2">Invalid link</h1>
        <p class="text-ink-500 mb-6">
          This password reset link is missing a token. Please request a new reset link.
        </p>
        <RouterLink
          to="/forgot-password"
          class="inline-flex items-center justify-center h-[52px] px-6 rounded-xl bg-ink-black text-white font-semibold hover:bg-ink-900 transition-colors"
        >
          Request new link
        </RouterLink>
        <div class="mt-6">
          <RouterLink
            to="/login"
            class="inline-flex items-center justify-center gap-2 text-sm text-ink-500 hover:text-ink-700 transition-colors"
          >
            <font-awesome-icon :icon="['fas', 'arrow-left']" />
            <span>Back to sign in</span>
          </RouterLink>
        </div>
      </div>
    </template>

    <!-- Success state -->
    <template v-else-if="isSuccess">
      <div class="text-center">
        <div class="w-16 h-16 bg-success-soft rounded-2xl flex items-center justify-center mx-auto mb-6">
          <font-awesome-icon :icon="['fas', 'check-circle']" class="text-3xl text-success-ink" />
        </div>
        <h1 class="text-2xl font-bold text-ink-900 mb-2">Password reset</h1>
        <p class="text-ink-500 mb-8">
          Your password has been reset. You can now sign in with your new password.
        </p>
        <RouterLink
          to="/login"
          class="inline-flex items-center justify-center w-full h-[52px] rounded-xl bg-ink-black text-white font-semibold hover:bg-ink-900 transition-colors"
        >
          Back to sign in
        </RouterLink>
      </div>
    </template>

    <!-- Form state -->
    <template v-else>
      <div class="text-center mb-7">
        <h1 class="text-2xl font-bold text-ink-900">Set new password</h1>
        <p class="text-ink-500 mt-1">Choose a strong password for your account.</p>
      </div>

      <div
        v-if="errorMessage"
        class="mb-5 p-4 bg-danger-soft border border-danger-ink/15 rounded-xl flex items-start gap-3"
      >
        <font-awesome-icon :icon="['fas', 'circle-exclamation']" class="text-danger-ink mt-0.5" />
        <div class="text-sm text-danger-ink">
          <p>{{ errorMessage }}</p>
          <RouterLink
            v-if="errorMessage.includes('expired')"
            to="/forgot-password"
            class="inline-block mt-2 font-medium text-danger-ink underline hover:opacity-80"
          >
            Request a new link
          </RouterLink>
        </div>
      </div>

      <form class="space-y-4" @submit.prevent="handleResetPassword">
        <AuthField
          v-model="password"
          label="NEW PASSWORD"
          :icon="['fas', 'lock']"
          type="password"
          autocomplete="new-password"
          required
          placeholder="Create a new password"
        >
          <template #hint>
            <div v-if="password.length > 0" class="flex items-center gap-2 pt-0.5">
              <div class="flex-1 flex gap-1">
                <div
                  v-for="i in 4"
                  :key="i"
                  class="h-1 flex-1 rounded-full"
                  :class="i <= passwordStrength.level ? passwordStrength.bar : 'bg-ink-200'"
                ></div>
              </div>
              <span class="text-[11.5px] font-semibold" :class="passwordStrength.textCls">
                {{ passwordStrength.text }}
              </span>
            </div>
            <p v-else class="text-xs text-ink-400">Must be at least 8 characters</p>
          </template>
        </AuthField>

        <AuthField
          v-model="confirmPassword"
          label="CONFIRM PASSWORD"
          :icon="['fas', 'lock']"
          type="password"
          autocomplete="new-password"
          required
          placeholder="Confirm your new password"
          :error="!!confirmPassword && !passwordsMatch"
        >
          <template #hint>
            <p v-if="confirmPassword && !passwordsMatch" class="text-xs text-danger-ink">
              Passwords do not match
            </p>
          </template>
        </AuthField>

        <button
          type="submit"
          :disabled="!isFormValid || isLoading"
          class="w-full h-[52px] rounded-xl bg-ink-black text-white font-semibold hover:bg-ink-900 disabled:opacity-50 transition-colors"
        >
          {{ isLoading ? 'Resetting…' : 'Reset password' }}
        </button>
      </form>

      <div class="mt-7 text-center">
        <RouterLink
          to="/login"
          class="inline-flex items-center justify-center gap-2 text-sm text-ink-500 hover:text-ink-700 transition-colors"
        >
          <font-awesome-icon :icon="['fas', 'arrow-left']" />
          <span>Back to sign in</span>
        </RouterLink>
      </div>
    </template>
  </div>
</template>
